import AOS from "aos";
import {isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration, useNavigate, useLocation} from "react-router";

import type {Route} from "./+types/root";
import {useCallback, useEffect, useState} from "react";
import {Toast, CookieModal, Page} from "@housebets/housebets-x-package";
import {useIntl} from "react-intl";
import {Footer} from "../app/components/Layout/Footer";
import {View} from "@housebets/housebets-x-package/lib/components/layout/Layout/View";
import {UnavailableModal, UnavailableSection} from "@housebets/housebets-x-package";
import {CurrencyToolbar} from "../app/components/Layout/Toolbar/CurrencyToolbar";
import {ProfileToolbar} from "../app/components/Layout/Toolbar/ProfileToolbar";
import {config} from "../app/config.js";
import {useWrapperData} from "../app/components/Layout/Wrapper/hooks/useWrapperData.js";
import {LoginType} from "../app/types/common";
import {SearchGameModal, AuthModal, WalletModal} from "../app/components/features";
import {ChatWrapper} from "../app/components/features/chat/ChatWrapper";
import {useWebSocketContext} from "../app/websocket/context/index.js";
import {Layout as L} from "@housebets/housebets-x-package";
import {useAppSelector} from "hooks/useAppSelector";
import DeviceDetector from "device-detector-js";
import apiAuth from "app/api/auth";
import apiUser from "app/api/user";
import {useDispatch} from "react-redux";
import {Coin} from "app/types/user";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import client from "app/api/client";
import {getUserMessageCountList} from "app/store/slices/message";
import {refreshFavoriteGamesData} from "app/store/slices/home";
import _debounce from "lodash/debounce";
import {
    setMaxChargeRate,
    setIsDeposit,
    setServerTime,
    setWeekRewardRate,
    setMonthRewardRate,
    setWeekRewardApprovedRate,
    setMonthRewardApprovedRate,
} from "app/store/slices/setting";
import {getUserTurnover, loginSuccess, refreshCashBackData, refreshRealCashBackData, setBalance, setOtherInfoUser} from "app/store/slices/user";
import {useLangNavigate} from "app/hooks/useLangNavigate";
import {useChangeLanguage} from "./hooks/useChangeLanguage";
import {setLocale} from "app/store/slices/config";
import {langOpt} from "app/components/constants/footer";
import useInitApp from "app/hooks/useInitApp";
import {Fa2Auth} from "./components/features/auth/Fa2Auth";

const deviceDetector = new DeviceDetector();

export const links: Route.LinksFunction = () => [
    {rel: "preconnect", href: "https://fonts.googleapis.com"},
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
    },
];

export function Layout({children}: {children: React.ReactNode}) {
    const {$t} = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [isShowModalCookie, setShowModalCookie] = useState(false);
    const dispatch = useDispatch();
    const {init, modal} = useGTMEvents();
    const langNavigate = useLangNavigate();

    const token = useAppSelector((state) => state.user.token);

    useInitApp();

    const [showScreenLoader, setShowScreenLoader] = useState(true);

    const removeScreenLoader = () => {
        setTimeout(() => {
            setShowScreenLoader(false);
        }, 50);

        const divToRemove = document.getElementById("loading");
        if (divToRemove) {
            document.body.removeChild(divToRemove);
        }
    };

    const debounceRemoveScreenLoader = useCallback(_debounce(removeScreenLoader, 250), []);

    useEffect(() => {
        const {pathname, search, hash} = location;
        if (pathname.startsWith("/en/")) {
            const newPath = pathname.replace(/^\/en/, "") || "/";
            navigate(newPath + search + hash, {replace: true});
        }
    }, [location, navigate]);

    useEffect(() => {
        setTimeout(() => {
            AOS.init({
                once: true,
                offset: -100,
            });
            AOS.refresh();
        }, 200);
    }, []);

    useEffect(() => {
        if (localStorage.getItem("access-cookie") === null) {
            setShowModalCookie(true);
        }
    }, []);

    const handleCloseCookie = () => {
        setShowModalCookie(false);
        localStorage.setItem("access-cookie", "1");
    };

    const {
        pathname,
        asideTabs,
        bottomMenu,
        socials,
        isSearchGameModalOpen,
        closeSearchGameModal,
        loginType,
        isAccessModalOpen,
        setIsAccessModalOpen,
        closeAuthModal,
    } = useWrapperData();

    const isLogin = useAppSelector((state) => state?.user?.isLogin);

    useEffect(() => {
        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };

        setVh();
        window.addEventListener("resize", setVh);

        return () => window.removeEventListener("resize", setVh);
    }, []);

    const {setWsUrl} = useWebSocketContext();
    const wsUrl = useAppSelector((state) => state.config.platformConfig?.webSocketUrl);

    useEffect(() => {
        if (wsUrl) {
            setWsUrl(wsUrl);
        }
    }, [wsUrl]);

    useEffect(() => {
        const locale = JSON.parse(localStorage.getItem("playing-config") || "")?.locale;
        const device = deviceDetector.parse(window.navigator.userAgent);

        const requestBalance = async () => {
            const {status, data} = await apiUser.getBalance();
            if (status === 200 && data.code === 0) {
                try {
                    const result = JSON.parse(data.data);
                    const list: Coin[] = result?.data || [];
                    dispatch(setBalance(list));
                } catch (error) {
                    // empty
                }
            }
        };

        const getUserInfo = async () => {
            const {status, data} = await apiAuth.getUserInfo();
            if (status === 200 && data.code === 0) {
                init({
                    locale,
                    user_id: data.data.userId,
                    device: device.device?.type,
                });
                dispatch(loginSuccess({...data.data, token}));

                const res = await apiUser.getOtherUserData(data.data.userId);
                if (res.status === 200 && res.data.code === 0) {
                    dispatch(setOtherInfoUser(res.data.data));
                }
            }
            debounceRemoveScreenLoader();
        };

        const getOtherData = async () => {
            const {status, data} = await apiUser.getUserOtherData(token || "");
            const {maxChargeRewardRate, isDeposit, serverTime, weekRewardRate, monthRewardRate} = data.data;
            if (status === 200 && data.code === 0) {
                dispatch(setMaxChargeRate(maxChargeRewardRate));
                dispatch(setIsDeposit(isDeposit));
                dispatch(setServerTime(serverTime));
                dispatch(setWeekRewardRate(weekRewardRate));
                dispatch(setMonthRewardRate(monthRewardRate));
            }
        };

        const getVipMonthData = async () => {
            const {status, data} = await apiAuth.getVipMonthData();
            if (status === 200 && data.code === 0) {
                const {userWeekRewardRate, userMonthRewardRate} = data.data;
                dispatch(setWeekRewardApprovedRate(userWeekRewardRate));
                dispatch(setMonthRewardApprovedRate(userMonthRewardRate));
                console.log("userWeekRewardRate", userWeekRewardRate);
            }
        };

        if (token) {
            client.setToken(token);
            requestBalance();
            getUserInfo();
            getOtherData();
            getVipMonthData();
            dispatch(getUserTurnover());
            dispatch(getUserMessageCountList());
            dispatch(refreshCashBackData());
            dispatch(refreshRealCashBackData());
            dispatch(refreshFavoriteGamesData());
        } else {
            client.setToken("");
            debounceRemoveScreenLoader();
            init({
                locale,
                device: device.device?.type,
            });
        }
    }, [token]);

    const getBrowserLocale = () => {
        const browserLang = navigator.language.split("-")[0];
        return langOpt.filter((item) => item.id === browserLang).length ? browserLang : "en";
    };

    const changeLanguage = useChangeLanguage();
    const locale = useAppSelector((state) => state.config.locale);

    useEffect(() => {
        if (locale === "en") {
            return;
        }
        if (locale && !location.pathname.startsWith("/" + locale)) {
            const cleanPathname = location.pathname.replace(/^\/(es|hu|fr|ru|pt|id|de|ko|tr|zh|vi|ja)(?=\/|$)/, "");
            navigate("/" + locale + cleanPathname);
        }
    }, [location]);

    useEffect(() => {
        const lang = getBrowserLocale();
        const autoSelectLang = localStorage.getItem("autoSelectLang");
        if (lang && autoSelectLang === null) {
            dispatch(setLocale(lang));
            changeLanguage(lang);
            localStorage.setItem("autoSelectLang", "1");
        }
    }, []);

    const [domLoaded, setDomLoaded] = useState(false);

    useEffect(() => {
        setDomLoaded(true);
    }, []);

    return (
        <>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <link rel="icon" href="/favicon.ico" type="image/x-icon" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />

                    {/* <meta property="og:title" content="Your Site Title" /> */}
                    {/* <meta property="og:description" content="Your site description here" /> */}
                    <meta property="og:image" content="/img/ogimage.jpg" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                    <meta property="og:image:alt" content="Simple registration, instant withdrawals, and exclusive perks" />
                    <meta property="og:type" content="website" />

                    <script src="https://telegram.org/js/telegram-widget.js" type="text/javascript" async></script>

                    {/* <meta property="og:url" content={window.location.host} /> */}
                    <Meta />
                    <Links />
                    {/* Google Tag Manager */}
                    <script dangerouslySetInnerHTML={{
                    __html: `
                        (function (w, d, s, l, i) {
                        w[l] = w[l] || [];
                        w[l].push({"gtm.start": new Date().getTime(), event: "gtm.js"});
                        var f = d.getElementsByTagName(s)[0],
                            j = d.createElement(s),
                            dl = l != "dataLayer" ? "&l=" + l : "";
                        j.async = true;
                        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                        f.parentNode.insertBefore(j, f);
                        })(window, document, "script", "dataLayer", "${config.gtm_container_id}");
                    `
                    }} />
                </head>
                <body>
                    {/* Google Tag Manager (noscript) */}
                    <noscript>
                        <iframe src={`https://www.googletagmanager.com/ns.html?id=${config.gtm_container_id}`} height="0" width="0" style={{ display: "none", visibility: "hidden" }}></iframe>
                    </noscript>
                    <L.Context isReady isScrollViewActive>
                        <View
                            headerCenter={<CurrencyToolbar />} // CurrencyToolbar
                            headerRight={<ProfileToolbar />} // ProfileToolbar
                            rightMenu={config.enable_chat && <ChatWrapper />}
                            tabs={asideTabs} // asideTabs
                            socials={socials} // socials
                            routeToHome={langNavigate("/")}
                            isAuthUser={isLogin}
                            tabsVersion={"v3"}
                            propsLogo={{
                                coin: $t({id: "housebetsCoin"}),
                                soon: $t({id: "soon"}),
                            }}
                            bottomMenu={bottomMenu} // bottomMenu
                            onLogoClick={() => {
                                window.open("https://docs.housebets.com/");
                            }}>
                            {children}
                            {/* <Outlet /> */}
                            <Page.Wrapper className="footer">
                                <Page.Row padding>
                                    <Footer />
                                </Page.Row>
                            </Page.Wrapper>
                        </View>
                        <ScrollRestoration />
                        <Scripts />

                        <WalletModal />
                        <AuthModal isOpen={loginType !== LoginType.HIDE} onClose={closeAuthModal} />
                        <SearchGameModal isOpen={isSearchGameModalOpen} setIsOpen={closeSearchGameModal} />
                        <UnavailableModal
                            isOpen={isAccessModalOpen}
                            onClose={() => {
                                setIsAccessModalOpen(false);
                            }}
                            afterOpenModal={() => {
                                modal.show("access");
                            }}
                            onAfterClose={() => {
                                modal.close("access");
                            }}>
                            <UnavailableSection title={$t({id: "UnavailableTitle"})} description={$t({id: "UnavailableDescription"})} socials={socials} />
                        </UnavailableModal>
                        <CookieModal
                            text={{
                                title: $t({id: "CookieModal.title"}),
                                description: $t({id: "CookieModal.description"}),
                                button: $t({id: "CookieModal.buttonText"}),
                            }}
                            isOpen={isShowModalCookie}
                            onClose={() => handleCloseCookie()}
                        />
                        <Fa2Auth />
                        <Toast />
                    </L.Context>
                </body>
            </html>
        </>
    );
}

export default function App() {
    return <Outlet />;
}

export function ErrorBoundary({error}: Route.ErrorBoundaryProps) {
    let message = "Oops!";
    let details = "An unexpected error occurred.";
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? "404" : "Error";
        details = error.status === 404 ? "The requested page could not be found." : error.statusText || details;
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message;
        stack = error.stack;
    }

    return (
        <main className="pt-16 p-4 container mx-auto">
            <h1>{message}</h1>
            <p>{details}</p>
            {stack && (
                <pre className="w-full p-4 overflow-x-auto">
                    <code>{stack}</code>
                </pre>
            )}
        </main>
    );
}
